import { Clear } from '@mui/icons-material';
import NfcIcon from '@mui/icons-material/Nfc';
import { Autocomplete, Box, Button, debounce, Paper, Tab, Tabs, TextField, Theme, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import { useMutation } from '@tanstack/react-query';
import React, { FormEvent, useCallback, useState } from 'react';
import { checkRFIDOrPersonIdForEvent, fetchPeopleForManualInput } from '../../api/mobileapi';
import { AutocompletePeopleData } from '../../api/types';
import { AdamsError, getErrorMessage } from '../../utils/errors';
import { FIRST_PAGE_INDEX } from '../../utils/utils';
import { HideInProduction } from '../HideInProduction/HideInProduction';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { NFCMockForm } from '../NFCMockForm/NFCMockForm';
import { NFCScan } from '../NFCScan/NFCScan';
import { Severity, useToast } from '../ToastProvider/ToastProvider';
import { useTranslations } from '../Translations/TranslationsProvider';

interface EventScanProps {
    eventId: string;
    refetchPeople: () => void;
}

const SNACKBAR_STYLE = {
    height: '50%',
};

const ALERT_STYLE = {
    padding: (theme: Theme) => theme.spacing(1.5, 2.5),
    fontSize: 'large',
    alignItems: 'center',
};

export const EvacuationScan: React.FC<EventScanProps> = ({ eventId, refetchPeople }) => {
    const { translations } = useTranslations();
    const { currentLanguage } = useHomeData();
    const toast = useToast();

    const [tabIndex, setTabIndex] = useState(0);
    const [selectedPerson, setSelectedPerson] = useState<AutocompletePeopleData | null>();

    const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
        setTabIndex(newIndex);
    };

    const showToast = (message: string, severity: Severity, borderColor: string) => {
        toast(message, severity, {
            sx: SNACKBAR_STYLE,
            alertSx: { ...ALERT_STYLE, border: `4px solid ${borderColor}` },
            verticalPosition: 'top',
        });
    };

    const { mutate: checkPerson } = useMutation(
        (rfidOrPersonId: string) =>
            checkRFIDOrPersonIdForEvent({ rfidOrPersonId, eventId: eventId!, language: currentLanguage }),
        {
            onSuccess: (data) => {
                setSelectedPerson(null);
                showToast(data.result, Severity.success, 'darkgreen');
                refetchPeople();
            },
            onError: (error: AdamsError) => {
                const message = getErrorMessage(error);
                if (message) showToast(message, Severity.success, 'darkred');
            },
        }
    );

    const handleError = useCallback((err: string) => {
        toast(err, Severity.error);
    }, []);

    const handleDetect = useCallback(
        (result: any) => {
            if (result && eventId) {
                checkPerson(result);
            } else {
                handleError(translations.EVACUATIONS_INVALID_SCAN);
            }
        },
        [handleError, checkPerson]
    );

    const handleCheckInByManualInput = (e: FormEvent) => {
        e.preventDefault();
        checkPerson(String(selectedPerson?.personID));
    };

    const mutation = useMutation({
        mutationFn: (query: string) =>
            fetchPeopleForManualInput({
                language: currentLanguage,
                query,
                page: FIRST_PAGE_INDEX,
            }),
    });

    const debouncedSearch = useCallback(
        debounce((hint: string) => {
            if (hint.length > 0) {
                mutation.mutate(hint);
            }
        }, 500),
        []
    );

    const handleManualInput = (event: React.ChangeEvent<{}>, newValue: AutocompletePeopleData | null) => {
        setSelectedPerson(newValue);
    };

    return (
        <Paper elevation={3} sx={{ mb: 2 }}>
            <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth">
                <Tab label={translations.EVACUATIONS_BADGE} />
                <Tab data-testid="event-manual-input-tab" label={translations.EVACUATIONS_MANUAL} />
            </Tabs>

            <Box p={2}>
                {tabIndex === 0 ? (
                    <Box textAlign="center" my={1}>
                        <HideInProduction>
                            <NFCMockForm onSubmit={handleDetect} />
                        </HideInProduction>

                        <NFCScan onScan={handleDetect} onError={handleError} />
                        <Box textAlign="center" my={1}>
                            <NfcIcon
                                style={{
                                    fontSize: '4rem',
                                    marginBottom: '1.5rem',
                                    color: blue[600],
                                }}
                            />
                            <Typography variant="h6">{translations.MENU_LINK_SCAN_NFC}</Typography>
                        </Box>
                    </Box>
                ) : (
                    <form onSubmit={handleCheckInByManualInput}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Autocomplete
                                //@ts-ignore
                                key={selectedPerson}
                                value={selectedPerson}
                                fullWidth
                                onChange={handleManualInput}
                                onInputChange={(event, newInputValue) => {
                                    debouncedSearch(newInputValue);
                                }}
                                isOptionEqualToValue={(option, value) => option.personID === value.personID}
                                options={mutation.isSuccess ? mutation.data?.persons : []}
                                filterOptions={(options) => options}
                                getOptionLabel={(option: AutocompletePeopleData) =>
                                    `${option.personFirstName} ${option.personLastName} (${option.personID})`
                                }
                                loading={mutation.isLoading}
                                PaperComponent={({ children }) => (
                                    <Paper style={{ background: '#f2f2f2' }}>{children}</Paper>
                                )}
                                renderOption={(props, option: AutocompletePeopleData) => (
                                    <ListItem {...props} sx={{ display: 'flex', alignItems: 'center', paddingY: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1">
                                                {option.personFirstName} {option.personLastName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {option.personOrgUnit && option.personOrgUnit}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Person"
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { paddingRight: '10px' },
                                            endAdornment: (
                                                <>
                                                    {selectedPerson && (
                                                        <IconButton
                                                            onClick={() => {
                                                                setSelectedPerson(null);
                                                            }}
                                                        >
                                                            <Clear />
                                                        </IconButton>
                                                    )}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Button data-testid="event-scan-button" type="submit" variant="contained" color="primary">
                                {translations.GLOBAL_SUBMIT_BUTTON}
                            </Button>
                        </Box>
                    </form>
                )}
            </Box>
        </Paper>
    );
};
